/**
 * Routing key extraction for `hatif_webhook_events` Realtime rows.
 *
 * The company-wide subscription receives every webhook the company logs.
 * The router needs a stable key it can match against React Query cache
 * subtrees to decide which phone's timeline to sync. WhatsApp webhooks
 * carry `conversation_id` natively; call webhooks don't get a
 * conversation (the persist layer sets it to `null`), so we route only
 * whatsapp events and ignore calls. If Voxa ever ships a call-side
 * conversation we add a second key kind here.
 */

import type { Json } from '@/integrations/supabase/types';

/** Minimal shape of the row Supabase Realtime delivers — only the
 * fields the router actually reads. The `[key: string]` index signature
 * declares it as a JSON Postgres row so it can flow into
 * `subscribeTableRows<HatifWebhookEventRow>` without widening (Supabase
 * `.on<T>` requires a JSON-row constraint). */
export interface HatifWebhookEventRow {
  event_type: 'call' | 'whatsapp';
  conversation_id: string | null;
  [key: string]: Json | undefined;
}

export type HatifEventRoutingKey =
  | { kind: 'conversation'; conversationId: string }
  | { kind: 'none' };

export function extractRoutingKey(
  row: HatifWebhookEventRow
): HatifEventRoutingKey {
  if (row.event_type === 'whatsapp' && row.conversation_id) {
    return { kind: 'conversation', conversationId: row.conversation_id };
  }
  return { kind: 'none' };
}
